import { isProd, type MetaField } from '$lib/utils';
import { createAllFilterData, executeSearch } from '$lib/utils/search';
import type { PageLoad } from './$types';
import {
	PUBLIC_DEFAULT_SHOPIFY_URL,
	PUBLIC_SHOPIFY_STOREFRONT_API_TOKEN,
	PUBLIC_SUPPORTED_LANGUAGES
} from '$env/static/public';
import { error, redirect } from '@sveltejs/kit';
import { resolveRoute } from '$app/paths';
import { supplyPathParamsWithLocalization } from '$lib/utils/i18n';
import { mapToNode } from '$lib/utils/product';
import { collectTreePath, getCollectionPath } from '$lib/utils/collectionTree';
import { getStory } from '$lib/storyblok/storyblok';
import { categoryRoots } from '../../../params/generated/category-roots';
import { LOCALE_COUNTRY_PRERENDER_MAPPING } from '$lib/i18n/constants';
import { CollectionStore } from '$houdini';

export const load = (async (context) => {
	const parentData = await context.parent();
	const handle = parentData.collectionData?.collection?.handle;

	if (!handle) {
		error(404, 'Collection Not Found');
	}

	if (
		![
			...categoryRoots,
			'sprenger-me-root',
			'pferdesport-root',
			'hundesport-root',
			'bootsport-root',
			'neuheiten-root'
		].includes(handle) &&
		context.params.handle.split('/').length <= 1
	) {
		const tree = collectTreePath(parentData.collectionTree, handle);
		const newHandle = tree
			.map((e) => e.link)
			.filter(Boolean)
			.join('/');
		redirect(
			307,
			resolveRoute('/[locale]-[country]/[...handle]', {
				...context.params,
				handle: newHandle
			})
		);
	}

	const filterData = createAllFilterData(context.url, handle, parentData.collectionTree);

	const [{ facetResponse, response }, collectionInfo] = await Promise.all([
		executeSearch(filterData, context.fetch, context.params.locale),
		getStory(`collections/${handle}`, context.params.locale)
	]);

	const languages = PUBLIC_SUPPORTED_LANGUAGES?.split(',') || [];
	const collectionHandle = context.params.handle.split('/').pop();

	const translations = await Promise.all(
		languages.map(async (locale) => {
			const store = new CollectionStore();
			await store.fetch({
				event: context,
				variables: { locale: locale.toUpperCase(), country: 'DE', handle: collectionHandle }
			});
			const data = await new Promise((resolve, reject) => {
				store.subscribe(({ fetching, data, errors }) => {
					if (!fetching) {
						if (data) {
							resolve(data);
						} else {
							reject(errors);
						}
					}
				});
			});
			return [locale, data];
		})
	);
	const translationMap = Object.fromEntries(translations);

	function hrefForLocale(locale: string, country: string) {
		const parts = [
			'https://www.sprenger.de',
			`${locale}-${country}`,
			...JSON.parse(translationMap[locale].collection.path.value).map((s) => s.url_key),
			collectionHandle
		];
		return parts.join('/');
	}

	parentData.canonical.set([
		{
			rel: 'canonical',
			href: hrefForLocale('de', 'DE')
		},
		...LOCALE_COUNTRY_PRERENDER_MAPPING.filter(
			({ locale, country }) => `${locale}-${country}` !== `de-DE`
		).map(({ locale, country }) => ({
			rel: 'alternate' as const,
			hreflang: `${locale}-${country}`,
			href: hrefForLocale(locale, country)
		}))
	]);

	return {
		...parentData,
		searchResponse: response,
		facetResponse,
		filterData,
		collectionInfo: collectionInfo.data
	};
}) satisfies PageLoad;

const fetchHandles = async (after?: string) => {
	const handleData = (await fetch(
		`https://${PUBLIC_DEFAULT_SHOPIFY_URL}/api/2023-10/graphql.json`,
		{
			body: JSON.stringify({
				operationName: 'CollectionHandles',
				query: `query CollectionHandles {
				collections(first: 250) {
					edges {
						node {
							handle
							path: metafield(namespace: "sprenger", key: "path") {
								key
								value
							}
						}
					}
				}
			}`
			}),
			headers: {
				'Content-Type': 'application/json',
				'X-Shopify-Storefront-Access-Token': PUBLIC_SHOPIFY_STOREFRONT_API_TOKEN,
				'Accept-Language': 'de-DE,de;q=0.9,en-US;q=0.8,en;q=0.7'
			},
			method: 'POST'
		}
	).then((r) => r.json())) as {
		data: {
			collections: { edges: { node: { handle: string; path: MetaField } }[] };
		};
	};
	if (!handleData?.data) {
		throw Error(`Unable to fetch handles: ${JSON.stringify(handleData)}`);
	}
	return {
		collections: handleData.data.collections
	};
};

export const entries = async () => {
	const { collections } = await fetchHandles();
	const results = mapToNode(collections).map(({ handle, path }) => ({
		handle,
		path: getCollectionPath({
			edges: [{ node: { path } }]
		})
	}));
	const allVariants = results
		.map((result) => {
			return {
				handle: [...result.path.map((s) => s.url_key), result.handle].join('/')
			};
		})
		.filter((entry) => !!entry.handle);

	return supplyPathParamsWithLocalization(allVariants);
};

export const prerender = isProd() ? 'auto' : false;
